import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { api, formatApiError } from '../App';
import {
  Layers, Plus, Loader2, Sparkles, Users, Ruler, MapPin, ArrowRight, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';

const fmtNum = (v) => (v == null ? '—' : new Intl.NumberFormat('en-IN').format(v));
const fmtMoney = (v) => (v == null ? '—' : `₹${new Intl.NumberFormat('en-IN').format(Math.round(v))}`);

export default function Projects({ user, setUser }) {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/projects');
      setProjects(res.data.projects || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const seedPalmSprings = async () => {
    try {
      setSeeding(true);
      const res = await api.post('/projects/seed-palm-springs');
      toast.success(res.data.message || 'Seeded');
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSeeding(false);
    }
  };

  const totalArea = projects.reduce((s, p) => s + (p.total_area_sq_yds || 0), 0);
  const totalBlocks = projects.reduce((s, p) => s + (p.block_count || 0), 0);

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-5" data-testid="projects-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-[#1a2e44] flex items-center gap-2">
              <Layers className="w-6 h-6 text-blue-600" /> Projects
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Development projects — plot layouts, apartment blocks, villa schemes
            </p>
          </div>
          <div className="flex items-center gap-2">
            {projects.length === 0 && (
              <Button
                variant="outline"
                onClick={seedPalmSprings}
                disabled={seeding}
                data-testid="seed-palm-springs-btn"
                className="border-blue-200 text-blue-700 hover:bg-blue-50"
              >
                {seeding ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
                Seed Palm Springs example
              </Button>
            )}
            <Button onClick={() => setShowCreate(true)} data-testid="new-project-btn" className="bg-[#1a2e44] hover:bg-[#243a55]">
              <Plus className="w-4 h-4 mr-1.5" /> New Project
            </Button>
          </div>
        </div>

        {/* Summary tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Tile label="Projects" value={projects.length} color="text-[#1a2e44]" />
          <Tile label="Total Blocks" value={fmtNum(totalBlocks)} color="text-blue-600" />
          <Tile label="Total Area (Sq.Yds)" value={fmtNum(Math.round(totalArea))} color="text-emerald-600" />
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : projects.length === 0 ? (
          <EmptyState onSeed={seedPalmSprings} onCreate={() => setShowCreate(true)} seeding={seeding} />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="projects-list">
            {projects.map(p => (
              <ProjectCard key={p.id} project={p} onOpen={() => navigate(`/projects/${p.id}`)} onDeleted={load} />
            ))}
          </div>
        )}

        <CreateProjectDialog
          open={showCreate}
          onClose={() => setShowCreate(false)}
          onCreated={(id) => { setShowCreate(false); navigate(`/projects/${id}`); }}
        />
      </div>
    </Layout>
  );
}

const Tile = ({ label, value, color }) => (
  <div className="bg-white rounded-lg border border-gray-100 px-4 py-3 shadow-sm">
    <p className="text-xs text-gray-500 mb-1">{label}</p>
    <p className={`text-xl font-semibold ${color}`}>{value}</p>
  </div>
);

const EmptyState = ({ onSeed, onCreate, seeding }) => (
  <div className="bg-white rounded-xl border border-dashed border-gray-300 p-10 text-center" data-testid="projects-empty">
    <Layers className="w-12 h-12 mx-auto text-blue-300 mb-3" />
    <h3 className="text-lg font-semibold text-gray-800">No projects yet</h3>
    <p className="text-sm text-gray-500 mt-1 max-w-md mx-auto">
      A project consolidates one or more master properties (e.g., J-3, J-4, S-3, S-4, VPK-3 → Palm Springs)
      and tracks per-block allotments to family members with area &amp; value.
    </p>
    <div className="flex items-center justify-center gap-2 mt-5">
      <Button variant="outline" onClick={onSeed} disabled={seeding} data-testid="empty-seed-btn">
        {seeding ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
        Seed Palm Springs example
      </Button>
      <Button onClick={onCreate} data-testid="empty-create-btn" className="bg-[#1a2e44] hover:bg-[#243a55]">
        <Plus className="w-4 h-4 mr-1.5" /> Create Project
      </Button>
    </div>
  </div>
);

const ProjectCard = ({ project: p, onOpen, onDeleted }) => {
  const [deleting, setDeleting] = useState(false);
  const onDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete project "${p.name}"? Linked properties will be unlinked but kept.`)) return;
    try {
      setDeleting(true);
      await api.delete(`/projects/${p.id}`);
      toast.success('Project deleted');
      onDeleted?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeleting(false);
    }
  };
  return (
    <div
      className="group bg-white rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all p-5 cursor-pointer"
      onClick={onOpen}
      data-testid={`project-card-${p.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-[#1a2e44] truncate">{p.name}</h3>
          {p.location && (
            <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1 truncate">
              <MapPin className="w-3.5 h-3.5 shrink-0" /> {p.location}
            </p>
          )}
        </div>
        <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-100 capitalize shrink-0">
          {p.project_type || 'plot'}
        </span>
      </div>
      {p.description && (
        <p className="text-xs text-gray-600 mt-3 line-clamp-2">{p.description}</p>
      )}
      <div className="grid grid-cols-3 gap-2 mt-4">
        <Mini icon={Layers} label="Blocks" value={fmtNum(p.block_count)} />
        <Mini icon={Ruler} label="Sq.Yds" value={fmtNum(Math.round(p.total_area_sq_yds || 0))} />
        <Mini icon={Users} label="Members" value={p.member_count || 0} />
      </div>
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
        <span className="text-xs text-gray-400">
          {p.estimated_total_value ? fmtMoney(p.estimated_total_value) : 'No rate set'}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={onDelete}
            disabled={deleting}
            className="text-gray-400 hover:text-red-600 p-1 rounded transition-colors"
            data-testid={`delete-project-${p.id}`}
            title="Delete project"
          >
            {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
          </button>
          <span className="text-blue-600 group-hover:translate-x-0.5 transition-transform inline-flex items-center text-sm">
            Open <ArrowRight className="w-4 h-4 ml-1" />
          </span>
        </div>
      </div>
    </div>
  );
};

const Mini = ({ icon: Icon, label, value }) => (
  <div className="bg-gray-50 rounded-lg px-2.5 py-1.5">
    <p className="text-[10px] uppercase tracking-wide text-gray-400 flex items-center gap-1">
      <Icon className="w-3 h-3" /> {label}
    </p>
    <p className="text-sm font-semibold text-[#1a2e44] mt-0.5">{value}</p>
  </div>
);

const CreateProjectDialog = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState({
    name: '', description: '', project_type: 'plot', location: '', default_rate_per_sqyd: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) setForm({ name: '', description: '', project_type: 'plot', location: '', default_rate_per_sqyd: '' });
  }, [open]);

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    try {
      setSaving(true);
      const payload = {
        ...form,
        default_rate_per_sqyd: form.default_rate_per_sqyd ? parseFloat(form.default_rate_per_sqyd) : null,
      };
      const res = await api.post('/projects', payload);
      toast.success('Project created');
      onCreated?.(res.data.id);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="create-project-dialog">
        <DialogHeader>
          <DialogTitle>New Project</DialogTitle>
          <DialogDescription>
            Consolidate one or more properties into a tracked development project.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <Field label="Name *">
            <input
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder="e.g., Palm Springs"
              data-testid="proj-name-input"
              className="w-full px-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-200 text-sm"
            />
          </Field>
          <Field label="Type">
            <select
              value={form.project_type}
              onChange={e => setForm({ ...form, project_type: e.target.value })}
              data-testid="proj-type-select"
              className="w-full px-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-200 text-sm bg-white"
            >
              <option value="plot">Plot Layout</option>
              <option value="apartment">Apartment</option>
              <option value="villa">Villa</option>
              <option value="mixed">Mixed</option>
            </select>
          </Field>
          <Field label="Location">
            <input
              value={form.location}
              onChange={e => setForm({ ...form, location: e.target.value })}
              placeholder="e.g., Kandukur Village, Maheshwaram Mandal"
              data-testid="proj-location-input"
              className="w-full px-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-200 text-sm"
            />
          </Field>
          <Field label="Default rate per Sq.Yd (optional)">
            <input
              type="number"
              value={form.default_rate_per_sqyd}
              onChange={e => setForm({ ...form, default_rate_per_sqyd: e.target.value })}
              placeholder="e.g., 25000"
              data-testid="proj-rate-input"
              className="w-full px-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-200 text-sm"
            />
          </Field>
          <Field label="Description">
            <textarea
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              rows={3}
              data-testid="proj-desc-input"
              className="w-full px-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-200 text-sm"
            />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="cancel-project-btn">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="save-project-btn" className="bg-[#1a2e44] hover:bg-[#243a55]">
            {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-medium text-gray-600 mb-1 block">{label}</span>
    {children}
  </label>
);
